/**
 * Analytics · User Adoption
 * Demo content - see src/demo/analytics.ts.
 */

import React from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, Kpi, PageHead, Table, Bar, Note, DemoBadge } from '../../components/ui';
import { ChartFrame, Trend, Bars, STATUS } from '../../components/charts';
import { ADOPTION } from '../../demo/analytics';

export const UserAdoptionPage: React.FC = () => (
  <AppShell
    title="User Adoption"
    sub="Analytics"
    actions={
      <>
        <DemoBadge />
        <button className="btn btn-g">Last 8 weeks</button>
        <button className="btn btn-p">Invite users</button>
      </>
    }
  >
    <PageHead
      eyebrow="Analytics"
      title="Who is actually using it"
      lede="Licences mean nothing; questions asked mean something. Adoption is measured by people who came back, broken down by the team they sit in."
    />

    <div className="g g4" style={{ marginBottom: 12 }}>
      {ADOPTION.kpis.map((k) => (
        <Kpi
          key={k.label}
          label={k.label}
          value={k.value}
          unit={'unit' in k ? (k as any).unit : undefined}
          color={k.color}
          icon={k.icon as any}
          pip={'pip' in k ? (k as any).pip : undefined}
          dir={('dir' in k ? (k as any).dir : '') as any}
          sub={'sub' in k ? (k as any).sub : undefined}
        />
      ))}
    </div>

    <div className="g g2" style={{ marginBottom: 12 }}>
      <ChartFrame title="Active users" sub="per week — returning users are the ones that count">
        <Trend
          data={ADOPTION.activity}
          x="week"
          series={[
            { key: 'active', label: 'Active' },
            { key: 'returning', label: 'Returning' },
          ]}
        />
      </ChartFrame>

      <ChartFrame title="Users by department" sub="weekly active">
        <Bars data={ADOPTION.byDepartment} x="department" horizontal series={[{ key: 'users', label: 'Users' }]} />
      </ChartFrame>
    </div>

    <div className="g g3">
      <div className="s2">
        <Card title="Adoption by role" sub="seats against people who came back" flush>
          <Table
            cols={[
              { t: 'Role' },
              { t: 'Seats', num: true },
              { t: 'Active', num: true },
              { t: 'Adoption' },
              { t: 'Questions', num: true },
            ]}
            rows={ADOPTION.roles.map((r) => [
              <b>{r.role}</b>,
              r.seats,
              r.active,
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Bar
                  pct={r.adoption}
                  width={60}
                  color={r.adoption >= 70 ? STATUS.good : r.adoption >= 50 ? STATUS.warning : STATUS.serious}
                />
                <span className="mono">{r.adoption}%</span>
              </span>,
              r.questions,
            ])}
          />
        </Card>
      </div>

      <Card title="Feature usage" sub="share of active users">
        {ADOPTION.features.map((f) => (
          <div className="prog-row" key={f.feature}>
            <span className="l">{f.feature}</span>
            <Bar
              pct={f.usage}
              width="100%"
              color={f.usage >= 70 ? STATUS.good : f.usage >= 30 ? STATUS.warning : STATUS.serious}
            />
            <span className="v">{f.usage}%</span>
          </div>
        ))}
        <Note style={{ marginTop: 10 }}>
          <b>HR is the gap.</b> 35% adoption on 40 seats, and the questions they do ask are the ones
          Workday answers — worth a session with that team before renewing the seats.
        </Note>
      </Card>
    </div>
  </AppShell>
);
